import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, Client, Dog, DogServiceEnrollment, Service, CreateClientInput } from "../lib/api";
import { useResponsive } from "../hooks/useResponsive";
import { EmptyState, IconPlus, IconUsers } from "../components/shared";

interface CreateClientFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

function CreateClientForm({ onClose, onSuccess }: CreateClientFormProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateClientInput) => api.clients.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create client");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError("First name, last name, and email are required");
      return;
    }

    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    createMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      tags: tags.length > 0 ? tags : null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4 text-slate-800">Add New Client</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-slate-700">
                First name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-slate-700">
                Last name
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-slate-700">
              Phone (optional)
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-slate-700">
              Tags (comma-separated)
            </label>
            <input
              id="tags"
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="VIP, Regular, New"
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? "Creating..." : "Create Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DogWithEnrollments extends Dog {
  enrollments?: DogServiceEnrollment[];
}

interface ClientCardProps {
  client: Client;
  dogs: DogWithEnrollments[];
  services: Map<string, Service>;
}

function ClientCard({ client, dogs, services }: ClientCardProps) {
  return (
    <Link
      to={`/clients/${client.id}`}
      className="block bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow no-underline"
    >
      <div className="mb-3">
        <div className="text-base font-semibold text-slate-800">
          {client.firstName} {client.lastName}
        </div>
        <div className="text-sm text-slate-500 mt-0.5">{client.email}</div>
      </div>

      {/* Dogs section */}
      {dogs.length > 0 && (
        <div className="flex flex-col gap-2 mb-3">
          {dogs.map((dog) => (
            <div key={dog.id} className="p-3 bg-slate-50 rounded-md">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🐕</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800">
                    {dog.name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {dog.breed || "Unknown breed"}
                  </div>
                </div>
                {/* Commands learned badge - would need to add this to Dog type */}
              </div>

              {/* Enrollment status badges */}
              {dog.enrollments && dog.enrollments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {dog.enrollments.map((enrollment) => {
                    const service = services.get(enrollment.appointmentTypeId);
                    const isActive = enrollment.status === "active";
                    const isCompleted = enrollment.status === "completed";

                    if (isActive) {
                      return (
                        <span
                          key={enrollment.id}
                          className="inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-[10px] font-medium text-blue-700 bg-blue-100"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          {service?.name || "Program"} · Appt #{enrollment.appointmentsCompleted}
                        </span>
                      );
                    }

                    if (isCompleted) {
                      return (
                        <span
                          key={enrollment.id}
                          className="inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-[10px] font-medium text-emerald-700 bg-emerald-100"
                        >
                          <span>✓</span>
                          {service?.name || "Program"}
                        </span>
                      );
                    }

                    return null;
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {client.tags && client.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {client.tags.map((tag, i) => (
            <span
              key={i}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        <span className="font-mono text-slate-500">{client.phone ?? "-"}</span>
        <div className="flex items-center gap-2">
          <span className="text-slate-500">{client.totalAppointments} appointments</span>
          {client.isBanned ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              Banned
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Active
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

type ClientFilter = "all" | "active" | "banned";

export function ClientsPage() {
  const { isMobile } = useResponsive();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clientFilter, setClientFilter] = useState<ClientFilter>("all");
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["clients", { page, search }],
    queryFn: () => api.clients.list({ page, limit: 20, search: search || undefined }),
  });

  // Fetch dogs for all clients on this page
  const clientIds = data?.data.map((c) => c.id) || [];
  const dogsQueries = useQuery({
    queryKey: ["dogs-by-clients", clientIds],
    queryFn: async () => {
      if (clientIds.length === 0) return new Map<string, DogWithEnrollments[]>();
      
      const results = await Promise.all(
        clientIds.map(async (clientId) => {
          try {
            const response = await api.dogs.byClient(clientId);
            return { clientId, dogs: response.data };
          } catch {
            return { clientId, dogs: [] };
          }
        })
      );

      const map = new Map<string, DogWithEnrollments[]>();
      for (const result of results) {
        map.set(result.clientId, result.dogs);
      }
      return map;
    },
    enabled: clientIds.length > 0,
  });

  // Fetch enrollments for all dogs
  const allDogIds = Array.from(dogsQueries.data?.values() || []).flat().map((d) => d.id);
  const enrollmentsQuery = useQuery({
    queryKey: ["enrollments-by-dogs", allDogIds],
    queryFn: async () => {
      if (allDogIds.length === 0) return new Map<string, DogServiceEnrollment[]>();
      
      const results = await Promise.all(
        allDogIds.map(async (dogId) => {
          try {
            const response = await api.dogServiceEnrollments.list({ dogId });
            return { dogId, enrollments: response.data };
          } catch {
            return { dogId, enrollments: [] };
          }
        })
      );

      const map = new Map<string, DogServiceEnrollment[]>();
      for (const result of results) {
        map.set(result.dogId, result.enrollments);
      }
      return map;
    },
    enabled: allDogIds.length > 0,
  });

  // Fetch services for enrollment badges
  const servicesQuery = useQuery({
    queryKey: ["services"],
    queryFn: async () => {
      const response = await api.services.list({ limit: 100 });
      const map = new Map<string, Service>();
      for (const service of response.data) {
        map.set(service.id, service);
      }
      return map;
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const handleCreateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["clients"] });
  };

  const filteredClients = data?.data.filter((client) => {
    if (clientFilter === "active") return !client.isBanned;
    if (clientFilter === "banned") return client.isBanned;
    return true;
  }) || [];

  const activeCount = data?.data.filter((c) => !c.isBanned).length || 0;
  const bannedCount = data?.data.filter((c) => c.isBanned).length || 0;

  // Build dogs with enrollments map
  const getDogsForClient = (clientId: string): DogWithEnrollments[] => {
    const dogs = dogsQueries.data?.get(clientId) || [];
    return dogs.map((dog) => ({
      ...dog,
      enrollments: enrollmentsQuery.data?.get(dog.id) || [],
    }));
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {error instanceof ApiError ? error.message : "Failed to load clients"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className={`border-b border-slate-200 bg-white ${isMobile ? "p-4" : "px-6 py-5"}`}>
        <div className="flex items-center justify-between mb-4">
          <h1 className={`font-bold text-slate-800 ${isMobile ? "text-xl" : "text-2xl"}`}>
            Clients
          </h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600"
          >
            <IconPlus size={16} />
            Add Client
          </button>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 max-w-md py-2.5 px-3.5 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700"
            >
              Clear
            </button>
          )}
        </form>

        {/* Filter tabs */}
        <div className="flex gap-2">
          <FilterTab
            label="All"
            count={data?.data.length || 0}
            active={clientFilter === "all"}
            onClick={() => setClientFilter("all")}
          />
          <FilterTab
            label="Active"
            count={activeCount}
            active={clientFilter === "active"}
            onClick={() => setClientFilter("active")}
          />
          <FilterTab
            label="Banned"
            count={bannedCount}
            active={clientFilter === "banned"}
            onClick={() => setClientFilter("banned")}
          />
        </div>
      </div>

      {/* Client Grid */}
      <div className={`flex-1 overflow-auto ${isMobile ? "p-3" : "px-6 py-5"}`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-slate-500">Loading clients...</div>
          </div>
        ) : filteredClients.length === 0 ? (
          search ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="text-slate-300 mb-4">
                <IconUsers size={64} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">No clients found</h3>
              <p className="text-slate-500 max-w-sm">
                No clients match "{search}". Try a different search term.
              </p>
            </div>
          ) : (
            <EmptyState
              title="No clients yet"
              description="Add your first client to get started managing appointments and services."
              actionLabel="Add Client"
              onAction={() => setShowCreateModal(true)}
            />
          )
        ) : (
          <>
            <div
              className={`grid gap-3 ${
                isMobile
                  ? "grid-cols-1"
                  : "grid-cols-[repeat(auto-fill,minmax(320px,1fr))]"
              }`}
            >
              {filteredClients.map((client) => (
                <ClientCard 
                  key={client.id} 
                  client={client} 
                  dogs={getDogsForClient(client.id)}
                  services={servicesQuery.data || new Map()}
                />
              ))}
            </div>

            {data && data.pagination.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  Page {data.pagination.page + 1} of {data.pagination.totalPages} ({data.pagination.total} clients)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pagination.totalPages - 1}
                    className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showCreateModal && (
        <CreateClientForm onClose={() => setShowCreateModal(false)} onSuccess={handleCreateSuccess} />
      )}
    </div>
  );
}

interface FilterTabProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

function FilterTab({ label, count, active, onClick }: FilterTabProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full text-sm font-medium transition-colors ${
        active
          ? "bg-blue-100 text-blue-700"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {label}
      <span className={`text-xs ${active ? "text-blue-500" : "text-slate-400"}`}>
        {count}
      </span>
    </button>
  );
}
